// ajay:code 11-08-26
import { useState, useEffect } from "react";
import { Plus, Users, Eye, EyeOff, Pencil, X } from "lucide-react";
import { superadminClientUserAPI, clientAPI } from "../../services/api";

export default function SuperClientUsers() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    client_id: "",
    status: "Active",
  });
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    superadminClientUserAPI
      .listAll()
      .then((r) => setUsers(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    clientAPI
      .list()
      .then((r) => setClients(r.data || []))
      .catch(() => {});
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({
      name: "",
      email: "",
      password: "",
      client_id: "",
      status: "Active",
    });
    setErr("");
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      client_id: u.client_id || "",
      status: u.status,
    });
    setErr("");
    setShowForm(true);
  };

  const submit = async () => {
    setErr("");
    if (!form.name.trim() || !form.email.trim())
      return setErr("Name and email are required");
    if (!editingId && !form.password.trim())
      return setErr("Password is required");

    setSaving(true);
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          email: form.email,
          client_id: form.client_id || null,
          status: form.status,
        };
        if (form.password.trim()) payload.password = form.password;
        await superadminClientUserAPI.update(editingId, payload);
      } else {
        await superadminClientUserAPI.create({
          name: form.name,
          email: form.email,
          password: form.password,
          client_id: form.client_id || null,
        });
      }
      setShowForm(false);
      load();
    } catch (e) {
      setErr(e.data?.message || e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 28 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>
            Client User Identities
          </h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Master list of client login accounts — companies link these to
            themselves afterwards
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={openAdd}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={14} /> Create Client User
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Client Org</th>
                <th>Status</th>
                <th>Last Login</th>
                <th style={{ width: 70 }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    No client users yet
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500 }}>{u.name}</td>
                  <td className="text2">{u.email}</td>
                  <td className="text2">{u.client_name || "—"}</td>
                  <td>
                    <span
                      className={`badge ${u.status === "Active" ? "badge-success" : "badge-danger"}`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="text2" style={{ fontSize: 12 }}>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <button
                      title="Edit"
                      onClick={() => openEdit(u)}
                      style={iconBtn}
                    >
                      <Pencil size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div
          onClick={() => setShowForm(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ maxWidth: 420, width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>
                {editingId ? "Edit Client User" : "Create Client User"}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text2)",
                }}
              >
                <X size={18} />
              </button>
            </div>

            {err && (
              <div
                style={{
                  color: "var(--danger)",
                  fontSize: 12,
                  marginBottom: 14,
                }}
              >
                {err}
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>
                Password{" "}
                {editingId && (
                  <span style={{ color: "var(--text3)", fontWeight: 400 }}>
                    (leave blank to keep current)
                  </span>
                )}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  style={{ paddingRight: 36 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "var(--text3)",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Client Organisation</label>
              <select
                value={form.client_id}
                onChange={(e) =>
                  setForm({ ...form, client_id: e.target.value })
                }
              >
                <option value="">— None —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            {editingId && (
              <div className="form-group" style={{ marginBottom: 18 }}>
                <label>Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}

            <button
              className="btn-primary"
              onClick={submit}
              disabled={saving}
              style={{ width: "100%" }}
            >
              {saving ? "Saving..." : editingId ? "Save Changes" : "Create"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: 6,
  padding: 5,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
